// GoalsStep.js - Third step: Set learning goals
import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';

const GOALS = [
  {
    id: 'light',
    label: 'Light',
    description: '15 min/day, Casual learning',
    icon: 'coffee',
  },
  {
    id: 'regular',
    label: 'Regular',
    description: '30 min/day, Steady progress',
    icon: 'book-open-variant',
    recommended: true,
  },
  {
    id: 'intensive',
    label: 'Intensive',
    description: '1 hour/day, Fast-track',
    icon: 'rocket-launch',
  },
];

const GoalsStep = ({ selectedGoal = 'regular', onGoalChange, onNext, onBack }) => {
  const { isDark } = useTheme();
  const [goal, setGoal] = useState(selectedGoal);

  useEffect(() => {
    setGoal(selectedGoal);
  }, [selectedGoal]);

  const colors = {
    bg: isDark ? '#1e293b' : '#f8fafc',
    card: isDark ? '#334155' : '#ffffff',
    text: isDark ? '#f8fafc' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    accent: '#2563eb',
    accentLight: isDark ? '#2563eb30' : '#2563eb15',
    border: isDark ? '#475569' : '#e2e8f0',
    selectedBorder: '#2563eb',
    selectedBg: isDark ? '#2563eb25' : '#2563eb10',
    success: '#10b981',
  };

  const selectGoal = (goalId) => {
    setGoal(goalId);
    onGoalChange?.(goalId);
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.bg }]}>
      <View style={[styles.content, { backgroundColor: colors.card, borderColor: colors.border }]}>
        {/* Title */}
        <Text style={[styles.title, { color: colors.text }]}>
          What's your learning goal?
        </Text>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
          You can change this anytime
        </Text>

        {/* Goal Options */}
        <View style={styles.goalsContainer}>
          {GOALS.map((g) => {
            const isSelected = goal === g.id;
            return (
              <TouchableOpacity
                key={g.id}
                style={[
                  styles.goalCard,
                  {
                    backgroundColor: isSelected ? colors.selectedBg : colors.bg,
                    borderColor: isSelected ? colors.selectedBorder : colors.border,
                  },
                ]}
                onPress={() => selectGoal(g.id)}
                activeOpacity={0.7}
              >
                {/* Radio indicator */}
                <View
                  style={[
                    styles.radio,
                    {
                      borderColor: isSelected ? colors.accent : colors.border,
                      backgroundColor: isSelected ? colors.accent : 'transparent',
                    },
                  ]}
                >
                  {isSelected && (
                    <View style={styles.radioInner} />
                  )}
                </View>

                {/* Icon */}
                <View
                  style={[
                    styles.iconWrapper,
                    {
                      backgroundColor: isSelected ? colors.accentLight : colors.bg,
                    },
                  ]}
                >
                  <MaterialCommunityIcons
                    name={g.icon}
                    size={24}
                    color={isSelected ? colors.accent : colors.textSecondary}
                  />
                </View>

                {/* Label and description */}
                <View style={styles.goalText}>
                  <View style={styles.labelRow}>
                    <Text
                      style={[
                        styles.goalLabel,
                        { color: isSelected ? colors.accent : colors.text },
                      ]}
                    >
                      {g.label}
                    </Text>
                    {g.recommended && (
                      <View style={[styles.recommendedBadge, { backgroundColor: colors.success + '20' }]}>
                        <Text style={[styles.recommendedText, { color: colors.success }]}>
                          Recommended
                        </Text>
                      </View>
                    )}
                  </View>
                  <Text style={[styles.goalDescription, { color: colors.textSecondary }]}>
                    {g.description}
                  </Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Navigation Buttons */}
        <View style={styles.buttonRow}>
          <TouchableOpacity
            style={[styles.secondaryButton, { borderColor: colors.border }]}
            onPress={onBack}
            activeOpacity={0.7}
          >
            <MaterialCommunityIcons name="arrow-left" size={18} color={colors.text} />
            <Text style={[styles.secondaryButtonText, { color: colors.text }]}>Back</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.primaryButton, { backgroundColor: colors.accent }]}
            onPress={onNext}
            activeOpacity={0.8}
          >
            <Text style={styles.primaryButtonText}>Continue</Text>
            <MaterialCommunityIcons name="arrow-right" size={18} color="#fff" />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  content: {
    width: '100%',
    maxWidth: 480,
    borderRadius: 16,
    padding: 32,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    textAlign: 'center',
    marginBottom: 28,
  },
  goalsContainer: {
    gap: 12,
    marginBottom: 28,
  },
  goalCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
    gap: 14,
  },
  radio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#fff',
  },
  iconWrapper: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
  },
  goalText: {
    flex: 1,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 2,
  },
  goalLabel: {
    fontSize: 16,
    fontWeight: '600',
  },
  recommendedBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  recommendedText: {
    fontSize: 11,
    fontWeight: '600',
  },
  goalDescription: {
    fontSize: 13,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  secondaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    borderWidth: 1,
    gap: 6,
  },
  secondaryButtonText: {
    fontSize: 15,
    fontWeight: '500',
  },
  primaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    gap: 6,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
});

export default GoalsStep;
